<template>
  <div class="range-slider">
    <div class="slider-track">
      <div class="slider-progress" :style="{ left: progressLeft + '%', width: progressWidth + '%' }"></div>
    </div>
    <input
      type="range"
      :min="min"
      :max="max"
      :value="value[0]"
      @input="onMinChange"
      :data-value="value[0]"
      class="range-input min-range"
    />
    <input
      type="range"
      :min="min"
      :max="max"
      :value="value[1]"
      @input="onMaxChange"
      :data-value="value[1]"
      class="range-input max-range"
    />
    <div class="slider-values">
      <span class="min-value" :class="{ overlap: badgesOverlap }" :style="{ left: (5 + progressLeft * 0.9) + '%' }">{{ formatValue(actualMin) }}</span>
      <span class="max-value" :style="{ left: (5 + (progressLeft + progressWidth) * 0.9) + '%' }">{{ formatValue(actualMax) }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RangeSlider',
  props: {
    value: {
      type: Array,
      required: true,
      default: () => [0, 100]
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    formatValue: {
      type: Function,
      default: (value) => value
    }
  },
  computed: {
    actualMin() {
      return Math.min(this.value[0], this.value[1])
    },
    actualMax() {
      return Math.max(this.value[0], this.value[1])
    },
    progressLeft() {
      return ((this.actualMin - this.min) / (this.max - this.min)) * 100
    },
    progressWidth() {
      return ((this.actualMax - this.actualMin) / (this.max - this.min)) * 100
    },
    badgesOverlap() {
      // Check if badges would overlap (less than 15% apart)
      return this.progressWidth < 15
    }
  },
  methods: {
    onMinChange(e) {
      let value = parseInt(e.target.value)
      // Allow handles to cross without swapping
      this.$emit('input', [value, this.value[1]])
    },
    onMaxChange(e) {
      let value = parseInt(e.target.value)
      // Allow handles to cross without swapping
      this.$emit('input', [this.value[0], value])
    }
  }
}
</script>

<style scoped>
.range-slider {
  position: relative;
  width: 100%;
  padding: 25px 0 25px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slider-values {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  font-weight: 600;
  font-size: 13px;
  color: #2a709a;
}

.min-value {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #2a709a, #316992);
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(42, 112, 154, 0.3);
  font-size: 11px;
  white-space: nowrap;
}

.max-value {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #2a709a, #316992);
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(42, 112, 154, 0.3);
  font-size: 11px;
  white-space: nowrap;
}

.min-value.overlap {
  top: 0;
  bottom: auto;
}

.max-value.overlap {
  top: 0;
  bottom: auto;
}

.slider-track {
  position: relative;
  width: 90%;
  height: 8px;
  background: linear-gradient(90deg, #e8e8e8, #f0f0f0);
  border-radius: 10px;
  margin: 25px auto 0 auto;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.slider-progress {
  position: absolute;
  height: 100%;
  background: linear-gradient(90deg, #2a709a, #316992, #4a7fa8, #6b9bc0);
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(42, 112, 154, 0.4);
}

.range-input {
  position: absolute;
  width: 90%;
  height: 8px;
  top: 45px;
  left: 5%;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
  appearance: none;
  margin: 0 auto;
}

.range-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #2a709a, #316992);
  border: 4px solid #fff;
  border-radius: 50%;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 4px 12px rgba(42, 112, 154, 0.5), 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.range-input::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(42, 112, 154, 0.6), 0 3px 6px rgba(0, 0, 0, 0.3);
}

.range-input::-webkit-slider-thumb:active {
  transform: scale(1.15);
  box-shadow: 0 8px 20px rgba(42, 112, 154, 0.7), 0 4px 8px rgba(0, 0, 0, 0.4);
}

.range-input::-webkit-slider-thumb::before {
  content: attr(data-value);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-12px);
  background: linear-gradient(135deg, #2a709a, #316992);
  color: #fff;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(42, 112, 154, 0.4);
}

.range-input::-webkit-slider-thumb::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  border: 6px solid transparent;
  border-top-color: #2a709a;
  pointer-events: none;
}

.range-input::-moz-range-thumb {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #2a709a, #316992);
  border: 4px solid #fff;
  border-radius: 50%;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 4px 12px rgba(42, 112, 154, 0.5), 0 2px 4px rgba(0, 0, 0, 0.2);
}

.min-range::-webkit-slider-thumb {
  background: linear-gradient(135deg, #2a709a, #316992);
}

.max-range::-webkit-slider-thumb {
  background: linear-gradient(135deg, #316992, #4a7fa8);
}

.min-range::-moz-range-thumb {
  background: linear-gradient(135deg, #2a709a, #316992);
}

.max-range::-moz-range-thumb {
  background: linear-gradient(135deg, #316992, #4a7fa8);
}

</style>
